import type { CMSMedia } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useState } from 'react'
import { CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useIntl } from '@/intl'
import { Carousel } from '@/scopes/common/components/Carousel'
import { FullScreenGallery } from '@/scopes/common/components/FullScreenGallery'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import styles from './RoomDetails.module.css'

interface RoomDetailsImage {
  src: string
  alt?: string
}

interface RoomDetailsProps {
  name: string
  images: RoomDetailsImage[]
  characteristics: string[]
  onImageClick: (index: number) => void
  onGallerySettle: () => void
}

export function RoomDetails({ name, images, characteristics, onImageClick, onGallerySettle }: RoomDetailsProps) {
  const intl = useIntl()
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const media: CMSMedia[] = images.map(image => ({
    src: image.src,
    alt: image.alt || name,
    type: CMSMediaType.IMAGE,
  }))

  const openGallery = (index: number) => {
    setSelectedImageIndex(index)
    onImageClick(index)
  }

  const closeGallery = () => setSelectedImageIndex(null)

  const renderImage = (image: RoomDetailsImage, index: number) => (
    <div key={index}>
      <button
        type="button"
        className={styles.imageButton}
        onClick={() => openGallery(index)}
        aria-label={intl.formatMessage({ id: 'photo_gallery_open_in_gallery' })}
      >
        <MediaServerLazyImage src={image.src} alt={image.alt || name} widths={[480]} />
      </button>
    </div>
  )

  return (
    <>
      <Typography className={styles.heading} variant="display-f-regular">{name}</Typography>
      {images.length === 1 && renderImage(images[0], 0)}
      {images.length > 1 && (
        <Carousel
          className={styles.gallery}
          showNavigation={false}
          showDots={false}
          dragFree
          slideSize="calc(var(--_room-image-width) + var(--primitives-space-8))"
          slideSpacing="var(--primitives-space-8)"
        >
          {images.map(renderImage)}
        </Carousel>
      )}
      {characteristics.length > 0 && (
        <ul className={styles.characteristics}>
          {characteristics.map((characteristic, index) => (
            <li key={index}>
              <Typography variant="body-m-regular-snug">{characteristic}</Typography>
            </li>
          ))}
        </ul>
      )}
      {selectedImageIndex !== null && (
        <FullScreenGallery
          open
          onClose={closeGallery}
          startFromIndex={selectedImageIndex}
          onSettle={onGallerySettle}
          media={media}
        />
      )}
    </>
  )
}
